import { Box, Typography, Chip } from '@mui/material';
import {
    Zap,
    FileText,
    FolderOpen,
    MessageCircle,
    ArrowRight,
    ExternalLink,
    Mail,
    Globe,
    Sparkles,
    Wheat,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const features = [
    {
        icon: Zap,
        title: 'Compare AIP Rates',
        description:
            'Pull rates from multiple Approved Insurance Providers side-by-side. Find the best coverage at the best price for your farmers — fast.',
        color: '#f59e0b',
        bg: '#fffbeb',
    },
    {
        icon: FileText,
        title: 'Generate Professional Quotes',
        description:
            'Create clean, easy-to-read quotes with multiple line items. Print or share them with your clients in seconds.',
        color: '#3b82f6',
        bg: '#eff6ff',
    },
    {
        icon: FolderOpen,
        title: 'Save & Organize Everything',
        description:
            'Keep your accounts, contacts, and quotes in one place. Track your progress and stay organized all season long.',
        isNew: true,
        color: '#10b981',
        bg: '#ecfdf5',
    },
    {
        icon: MessageCircle,
        title: 'AI-Powered Assistant',
        description:
            'Got questions about AIPs, hail manuals, or coverage options? Ask our AI chatbot and get answers instantly.',
        isNew: true,
        color: '#8b5cf6',
        bg: '#f5f3ff',
    },
];

export const Dashboard = () => {
    return (
        <Box sx={{ maxWidth: 960, mx: 'auto', py: 4, px: 2 }}>
            {/* Hero Section */}
            <Box
                sx={{
                    background:
                        'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 50%, #eff6ff 100%)',
                    borderRadius: '16px',
                    border: '1px solid #d1fae5',
                    p: { xs: 3, md: 5 },
                    mb: 4,
                    textAlign: 'center',
                }}
            >
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap={1.5}
                    mb={2}
                >
                    <Wheat size={32} color="#10b981" />
                    <Typography
                        sx={{
                            fontSize: {
                                xs: '1.5rem',
                                md: '2rem',
                            },
                            fontWeight: 800,
                            color: '#0f172a',
                            lineHeight: 1.2,
                        }}
                    >
                        Welcome to Crop West's
                        <br />
                        Hail Pricing Tool
                    </Typography>
                </Box>
                <Typography
                    sx={{
                        fontSize: '1.0625rem',
                        color: '#475569',
                        maxWidth: 600,
                        mx: 'auto',
                        mb: 3,
                        lineHeight: 1.6,
                    }}
                >
                    Compare hail insurance rates across every AIP we work with —
                    so you can help your farmers find the best coverages at the
                    best prices, all in one place.
                </Typography>
                <Box
                    display="flex"
                    gap={2}
                    justifyContent="center"
                    flexWrap="wrap"
                >
                    <Box
                        component={Link}
                        to="/hail-pricing-tool"
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 3,
                            py: 1.25,
                            borderRadius: '8px',
                            backgroundColor: '#10b981',
                            color: '#ffffff',
                            fontWeight: 600,
                            fontSize: '0.9375rem',
                            textDecoration: 'none',
                            transition: 'background-color 0.15s',
                            '&:hover': {
                                backgroundColor: '#059669',
                            },
                        }}
                    >
                        Get Started
                        <ArrowRight size={18} />
                    </Box>
                    <Box
                        component="a"
                        href="https://cropwestinsurance.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 3,
                            py: 1.25,
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                            backgroundColor: '#ffffff',
                            color: '#475569',
                            fontWeight: 600,
                            fontSize: '0.9375rem',
                            textDecoration: 'none',
                            transition: 'all 0.15s',
                            '&:hover': {
                                borderColor: '#10b981',
                                color: '#10b981',
                            },
                        }}
                    >
                        Visit Crop West
                        <ExternalLink size={16} />
                    </Box>
                </Box>
            </Box>

            {/* Feature Cards */}
            <Typography
                sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: '#94a3b8',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    mb: 2,
                }}
            >
                What you can do
            </Typography>
            <Box
                display="grid"
                gridTemplateColumns={{
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                }}
                gap={2}
                mb={4}
            >
                {features.map(feat => {
                    const Icon = feat.icon;
                    return (
                        <Box
                            key={feat.title}
                            sx={{
                                borderRadius: '12px',
                                border: '1px solid #e2e8f0',
                                backgroundColor: '#ffffff',
                                p: 3,
                                transition: 'box-shadow 0.15s, transform 0.15s',
                                '&:hover': {
                                    boxShadow:
                                        '0 4px 6px -1px rgb(0 0 0 / 0.07)',
                                    transform: 'translateY(-2px)',
                                },
                            }}
                        >
                            <Box
                                display="flex"
                                alignItems="center"
                                gap={1.5}
                                mb={1.5}
                            >
                                <Box
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: '10px',
                                        backgroundColor: feat.bg,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                    }}
                                >
                                    <Icon size={20} color={feat.color} />
                                </Box>
                                <Typography
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: '0.9375rem',
                                        color: '#0f172a',
                                    }}
                                >
                                    {feat.title}
                                </Typography>
                                {feat.isNew && (
                                    <Chip
                                        label="NEW"
                                        size="small"
                                        sx={{
                                            height: 20,
                                            fontSize: '0.625rem',
                                            fontWeight: 700,
                                            backgroundColor: '#d1fae5',
                                            color: '#047857',
                                            borderRadius: '4px',
                                        }}
                                    />
                                )}
                            </Box>
                            <Typography
                                sx={{
                                    fontSize: '0.8125rem',
                                    color: '#64748b',
                                    lineHeight: 1.6,
                                }}
                            >
                                {feat.description}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>

            {/* CropAMS Upsell */}
            <Box
                sx={{
                    borderRadius: '12px',
                    border: '1px solid #d1fae5',
                    background:
                        'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)',
                    p: { xs: 3, md: 4 },
                    mb: 4,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: -20,
                        right: -20,
                        width: 120,
                        height: 120,
                        borderRadius: '50%',
                        backgroundColor: '#10b98115',
                    }}
                />
                <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                    <Sparkles size={20} color="#10b981" />
                    <Typography
                        sx={{
                            fontSize: '0.6875rem',
                            fontWeight: 700,
                            color: '#047857',
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                        }}
                    >
                        Like what you see?
                    </Typography>
                </Box>
                <Typography
                    sx={{
                        fontSize: '1.25rem',
                        fontWeight: 700,
                        color: '#0f172a',
                        mb: 1,
                    }}
                >
                    Upgrade to CropAMS — The Complete Agency Management System
                </Typography>
                <Typography
                    sx={{
                        fontSize: '0.9375rem',
                        color: '#475569',
                        mb: 2.5,
                        maxWidth: 640,
                        lineHeight: 1.6,
                    }}
                >
                    Built by seasoned crop insurance agents, for crop insurance
                    agents. CropAMS gives you everything you need to run your
                    agency — leads, policies, action items, calendar, email, and
                    more. There's nothing else like it.
                </Typography>
                <Box
                    component="a"
                    href="https://www.cropams.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 3,
                        py: 1.25,
                        borderRadius: '8px',
                        backgroundColor: '#10b981',
                        color: '#ffffff',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        textDecoration: 'none',
                        transition: 'background-color 0.15s',
                        '&:hover': {
                            backgroundColor: '#059669',
                        },
                    }}
                >
                    Learn More About CropAMS
                    <ExternalLink size={16} />
                </Box>
            </Box>

            {/* Help Footer */}
            <Box
                sx={{
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    backgroundColor: '#f8fafc',
                    p: 3,
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    justifyContent: 'space-between',
                    gap: 2,
                }}
            >
                <Box>
                    <Typography
                        sx={{
                            fontWeight: 600,
                            fontSize: '0.9375rem',
                            color: '#0f172a',
                            mb: 0.5,
                        }}
                    >
                        Need help getting started?
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: '0.8125rem',
                            color: '#64748b',
                        }}
                    >
                        We're here for you — reach out any time.
                    </Typography>
                </Box>
                <Box display="flex" gap={2} flexWrap="wrap">
                    <Box
                        component="a"
                        href="mailto:support@hailhelper.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 2,
                            py: 1,
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                            backgroundColor: '#ffffff',
                            color: '#475569',
                            fontSize: '0.8125rem',
                            fontWeight: 500,
                            textDecoration: 'none',
                            transition: 'all 0.15s',
                            cursor: 'pointer',
                            '&:hover': {
                                borderColor: '#10b981',
                                color: '#10b981',
                            },
                        }}
                    >
                        <Mail size={16} />
                        support@hailhelper.com
                    </Box>
                    <Box
                        component="a"
                        href="https://cropwestinsurance.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 2,
                            py: 1,
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                            backgroundColor: '#ffffff',
                            color: '#475569',
                            fontSize: '0.8125rem',
                            fontWeight: 500,
                            textDecoration: 'none',
                            transition: 'all 0.15s',
                            '&:hover': {
                                borderColor: '#10b981',
                                color: '#10b981',
                            },
                        }}
                    >
                        <Globe size={16} />
                        cropwestinsurance.com
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};
